import { type CSSProperties, forwardRef, memo, useMemo } from 'react';

import { type CanvasNodeExportPayload } from '../utils/canvas-asset-drop';
import { getVideoThumbnailUrl } from '../utils/getVideoThumbnailUrl';

const PREVIEW_MAX = 240;
const CONTAINER_PADDING = 8;
const STACK_OFFSET_MULTI = 4;
const STACK_OFFSET_MANY = 8;

/** Build a preview image URL from an export payload, generating an imgix video thumbnail when needed. */
const getPreviewUrl = (payload: CanvasNodeExportPayload): string | undefined => {
  if (payload.mediaType !== 'video') {
    return payload.media.image ?? payload.media.original;
  }

  if (payload.thumbnailUrl) {
    return payload.thumbnailUrl;
  }

  const videoUrl = payload.media.original;

  return videoUrl ? getVideoThumbnailUrl(videoUrl) : undefined;
};

export type CanvasExportDragBadgeProps = {
  initialX: number;
  initialY: number;
  payloads: CanvasNodeExportPayload[];
};

export const CanvasExportDragBadge = memo(
  forwardRef<HTMLDivElement, CanvasExportDragBadgeProps>(({ initialX, initialY, payloads }, ref) => {
    const count = payloads.length;
    const first = payloads[0];
    const imageUrl = getPreviewUrl(first);

    const { containerW, containerH, stackOffset } = useMemo(() => {
      const ratio = first.width / first.height;
      const thumbW = ratio >= 1 ? PREVIEW_MAX : Math.round(PREVIEW_MAX * ratio);
      const thumbH = ratio >= 1 ? Math.round(PREVIEW_MAX / ratio) : PREVIEW_MAX;
      const containerW = thumbW + CONTAINER_PADDING;
      const containerH = thumbH + CONTAINER_PADDING;

      let stackOffset = 0;

      if (count > 2) {
        stackOffset = STACK_OFFSET_MANY;
      } else if (count > 1) {
        stackOffset = STACK_OFFSET_MULTI;
      }

      return { containerH, containerW, stackOffset };
    }, [first.width, first.height, count]);

    const wrapperStyle = useMemo(
      (): CSSProperties => ({
        height: containerH + stackOffset,
        left: initialX,
        top: initialY,
        width: containerW + stackOffset,
      }),
      [containerH, containerW, initialX, initialY, stackOffset],
    );

    const cardStyle = useMemo(
      (): CSSProperties => ({
        height: containerH,
        width: containerW,
      }),
      [containerH, containerW],
    );

    return (
      <div
        className="pointer-events-none fixed z-portal -translate-x-1/2 -translate-y-1/2"
        ref={ref}
        style={wrapperStyle}
      >
        {count > 2 && (
          <div className="absolute top-2 left-2 rounded-16 bg-grey-1 shadow-drag-preview" style={cardStyle} />
        )}
        {count > 1 && (
          <div className="absolute top-1 left-1 rounded-16 bg-grey-1 shadow-drag-preview" style={cardStyle} />
        )}
        <div
          className="absolute top-0 left-0 overflow-hidden rounded-16 bg-grey-1 shadow-drag-preview"
          style={cardStyle}
        >
          {imageUrl && (
            // biome-ignore lint/performance/noImgElement: canvas node URL is a direct media URL, not eligible for Next.js Image optimization
            <img
              alt=""
              className="absolute inset-1 h-[calc(100%-8px)] w-[calc(100%-8px)] rounded-12 object-cover"
              draggable={false}
              src={imageUrl}
            />
          )}
          {count > 1 && (
            <div className="absolute top-3 right-3 flex h-6 items-center rounded-4 bg-black/70 px-1.5">
              <span className="font-semibold text-12 text-white">{count} assets</span>
            </div>
          )}
        </div>
      </div>
    );
  }),
);

CanvasExportDragBadge.displayName = 'CanvasExportDragBadge';
